import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback Component
 * Handles OAuth redirect from Emergent Auth
 * Processes session_id from URL fragment, exchanges it for user data,
 * stores auth tokens, and redirects to homepage
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuthCallback = async () => {
      try {
        // Extract session_id from URL fragment (after #)
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1)); // Remove leading #
        const sessionId = params.get('session_id');

        console.log('[OAuth] URL hash:', hash);
        console.log('[OAuth] Session ID:', sessionId ? 'Found' : 'Missing');

        if (!sessionId) {
          console.error('[OAuth] No session_id found in URL fragment');
          console.error('[OAuth] Full URL:', window.location.href);
          navigate('/login?error=auth_failed');
          return;
        }

        console.log('[OAuth] Exchanging session_id for user data...');

        // Exchange session_id for user data via backend
        const { data } = await axios.post(`${API}/api/auth/google/session`, {
          session_id: sessionId
        });

        console.log('[OAuth] User data received:', data.email);

        // Store tokens in localStorage (existing auth pattern)
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          console.log('[OAuth] Access token stored');
        }
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
          console.log('[OAuth] Refresh token stored');
        }

        console.log('[OAuth] Redirecting to homepage...');

        // Redirect to homepage with user data
        navigate('/', { 
          replace: true,
          state: { user: data }
        });

      } catch (error) {
        console.error('[OAuth] Callback error:', error);
        console.error('[OAuth] Error details:', error.response?.data);
        console.error('[OAuth] Error status:', error.response?.status);
        console.error('[OAuth] Error config URL:', error.config?.url);
        const backendDetail = error.response?.data?.detail;
        const status = error.response?.status;
        const errorMessage = backendDetail
          ? `${backendDetail} (HTTP ${status})`
          : error.message === 'Network Error'
            ? `Network error reaching ${API} — backend unreachable or CORS blocked`
            : `Authentication failed: ${error.message || 'unknown error'}`;
        navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
      }
    };

    processOAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] border-2 border-[#D4AF37] mx-auto flex items-center justify-center mb-4">
          <div className="w-8 h-10 bg-[#D4AF37]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
        </div>
        <p className="text-white text-lg">Signing you in...</p>
        <div className="mt-4">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
